import { supabase } from '../lib/supabase';
import type { Organization } from '../types';

export interface OrganizationData {
  name: string;
  address?: string | null;
  postalCode?: string | null;
  totalUnits?: number | null;
  fiscalYearStartMonth?: number;
  managementCompany?: string | null;
  establishedDate?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

/**
 * 組織管理サービス
 */
export class OrganizationService {
  /**
   * 新規組織を作成
   */
  static async createOrganization(data: OrganizationData): Promise<Organization> {
    const { data: organization, error } = await supabase
      .from('organizations')
      .insert({
        name: data.name,
        address: data.address || null,
        postal_code: data.postalCode || null,
        total_units: data.totalUnits || null,
        fiscal_year_start_month: data.fiscalYearStartMonth || 4,
        management_company: data.managementCompany || null,
        established_date: data.establishedDate || null,
        contact_email: data.contactEmail || null,
        contact_phone: data.contactPhone || null,
        settings: {},
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to create organization:', error);
      throw new Error('組織の作成に失敗しました');
    }

    return organization;
  }

  /**
   * 組織情報を取得
   */
  static async getOrganizationById(id: string): Promise<Organization> {
    const { data: organization, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Failed to fetch organization:', error);
      throw new Error('組織情報の取得に失敗しました');
    }

    return organization;
  }

  /**
   * 組織情報を更新
   */
  static async updateOrganization(
    id: string,
    data: Partial<OrganizationData>
  ): Promise<Organization> {
    const updateData: any = {};

    if (data.name !== undefined) updateData.name = data.name;
    if (data.address !== undefined) updateData.address = data.address;
    if (data.postalCode !== undefined) updateData.postal_code = data.postalCode;
    if (data.totalUnits !== undefined) updateData.total_units = data.totalUnits;
    if (data.fiscalYearStartMonth !== undefined) {
      updateData.fiscal_year_start_month = data.fiscalYearStartMonth;
    }
    if (data.managementCompany !== undefined) {
      updateData.management_company = data.managementCompany;
    }
    if (data.establishedDate !== undefined) {
      updateData.established_date = data.establishedDate;
    }
    if (data.contactEmail !== undefined) updateData.contact_email = data.contactEmail;
    if (data.contactPhone !== undefined) updateData.contact_phone = data.contactPhone;

    const { data: organization, error } = await supabase
      .from('organizations')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Failed to update organization:', error);
      throw new Error('組織情報の更新に失敗しました');
    }

    return organization;
  }

  /**
   * 組織の統計情報を取得
   */
  static async getOrganizationStats(id: string) {
    // メンバー数を取得
    const { count: memberCount, error: memberError } = await supabase
      .from('user_organizations')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', id);

    if (memberError) {
      console.error('Failed to fetch member count:', memberError);
    }

    // 取引件数を取得
    const { count: transactionCount, error: transactionError } = await supabase
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', id);

    if (transactionError) {
      console.error('Failed to fetch transaction count:', transactionError);
    }

    return {
      memberCount: memberCount || 0,
      transactionCount: transactionCount || 0,
    };
  }
}